export const meta = {
  id: '11-possessives-and-family',
  order: 11,
  titleFi: 'Omistussanat ja perhe',
  titleEn: 'Possessives and family',
  duolingoUnit: ['duo-11-possessive-adjectives', 'duo-12-family'],
  lemmaIds: [
    'pōja',
    'kona',
    'konor',
    'ziry',
    'ūja',
    'jāha',
    'jāhon',
    'lenton',
    'zȳha',
    'zȳhon',
    'valonqar',
    'lēkia',
    'hāedar',
    'mandia',
    'rūs',
    'tala',
    'qȳbor',
    'dubāzma',
    'dubys',
    'iāpa',
    'velma',
    'iāpanna',
    'idaña',
    'ñābranna',
    'ñāmar',
    'qȳbranna',
    'velmanna',
    'ours:virnon',
    'ours:nektortys',
    'ours:rūhonnon',
    'ours:mijejos',
  ],
  exampleIds: [
    'ex-cd113b1637ad',
    'hotd-s3e03-008',
    'ex-de5d41f375ba',
    'ex-6e8f46d09a69',
    'ex-cc7aa25c2069',
    'ex-521269ad7a00',
    'ex-98000386884c',
    'hotd-s1e01-016',
    'hotd-s1e01-019',
    'ex-6768c822f817',
    'ex-3326482153df',
    'ex-07dc454e8d2a',
    'ex-11be124ca5b8',
    'got-s6e01-002',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Possessive_adjectives',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Aquatic_class',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Pronouns',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Phonotactics',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Gender',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Family',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_kinship_terminology',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Noun_Declensions',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['possessives'],
  grammarAlso: ['declensions'],
  showLines: ['hotd-s3e03-008', 'hotd-s1e01-016', 'hotd-s1e01-019', 'got-s6e01-002'],
  pronunciation: { focus: 'palatals', words: ['idaña', 'ñābranna', 'ñāmar', 'muña', 'ñuha'], lines: ['got-s6e01-002'] },
  letter: {
    fi: 'Esittele perheesi: minun äitini, sinun isäsi, heidän lapsensa.',
    en: 'Introduce your family: my mother, your father, their children.',
  },
  scene: 'got-s6e01-1',
}

# Omistussanat ja perhe

Kaksi osaa, jotka kuuluvat yhteen: ensin omistussanat (minun, sinun, hänen), sitten perheen ja suvun sanat, joiden kanssa omistussanoja tarvitaan jatkuvasti.

## Omistussanat

Sana <Hv glossed>ñuha</Hv> ’minun’ on jo tuttu. Nyt vuorossa ovat kaikki persoonat. Wikin pronominisivun mukaan omistussanat ovat:

- <Hv glossed>ñuha</Hv> ’minun’, <Hv glossed>aōha</Hv> ’sinun’
- <Hv>zȳha</Hv> ja <Hv glossed>jāha</Hv> ’hänen, sen’ (näistä valitaan omistajan suvun mukaan, ks. alempana)
- <Hv glossed>īlva</Hv> ’meidän’, <Hv glossed>jeva</Hv> ’teidän’, <Hv glossed>pōja</Hv> ’heidän’

Ne ovat adjektiiveja. Ne mukautuvat omistettuun substantiiviin suvussa, luvussa ja sijassa niin kuin mikä tahansa adjektiivi. Duolingon Tips & Notes -ohjeissa sanotaan suoraan, että tästä eteenpäin jokainen adjektiivi pitää osata taivuttaa kaikissa tähän mennessä opituissa luvuissa, suvuissa ja sijoissa.

Tässä suomi ja valyria tekevät saman asian eri keinoin. Suomi merkitsee omistajan liitteellä itse substantiiviin, valyria erillisellä adjektiivilla substantiivin eteen:

<Compare glossed fi="talo|ni" hv="ñuh|on lenton" label="suomessa omistusliite, valyriassa adjektiivi" />

<Hv glossed>Lenton</Hv> ’talo, koti’ on maasukua, joten ’minun’ on maasuvun muodossa: <Seam l="ñuha" s="ñuh" e="on" />.

## Vesisuvun kolme kompastuskiveä

Omistussanoissa vesisuku on vaikein. Duolingon ohjeet nostavat esiin kolme yksikön ja monikon paria:

- <Hv l="ñuha">ñuhor</Hv> ~ <Hv glossed>ñurha</Hv> ’minun’
- <Hv l="pōja">pōjor</Hv> ~ <Hv glossed>pōja</Hv> ’heidän’
- <Hv l="kona">konor</Hv> ~ <Hv glossed>kōdra</Hv> ’nuo’

Kaikissa kolmessa syy on sama. Vesisuvun monikossa vartalon perään tulee r ja sen jälkeen -a, eikä r sovi kaikkien konsonanttien perään.

**1. h + r → rh.** Tämä on tuttu vesisuvun oppitunnilta. Odotettu *ñuhra* kirjoitetaan <Hv>ñurha</Hv> ja äännetään sen mukaisesti.

**2. j + r → j.** Yhdistelmä jr ei ole valyriassa mahdollinen, joten r katoaa kokonaan: <Seam l="pōja" s="pōj" e="or" /> → <Seam l="pōja" s="pōj" e="a" />. Ohjeiden mukaan samoin käy yhdistelmille sr ja zr, jotka muuttuvat j:ksi. Wikin äännerakennesivu tarkentaa, että sr:lle näin käy usein mutta ei aina.

**3. <Term id="nasaali">nasaali</Term> + r.** Tämä kannattaa painaa mieleen, koska ilmiö on valyriassa yleinen. Kun n:n tai m:n perään tulee suoraan r, nasaali katoaa, edeltävä vokaali pitenee ja tilalle tulee samassa kohdassa äännettävä <Term id="soinnillinen">soinnillinen</Term> <Term id="klusiili">klusiili</Term>: n:n tilalle d ja m:n tilalle b. Alkuperäisestä muodosta *konra* tuli ensin *kondra* ja lopulta <Hv l="kona" fi="nuo">kōdra</Hv>:

<Seam l="kona" s="kon" e="or" /> → <Seam l="kona" s="kōdr" e="a" fi="nuo" />

<Pronounce l="kona" word="kōdra" fi="nuo" />

Sama sääntö tulee vastaan substantiiveissa. Tämän oppitunnin perhesanoissa <Hv glossed>ñāmar</Hv> ’isän nuorempi sisar’ saa muodon <Hv>ñābri</Hv>, ja eläinten oppitunnilla (<LessonLink n={13}>13</LessonLink>) <Hv glossed>bianor</Hv> ’uuhi’ saa monikon <Hv>biādra</Hv>.

<Paradigm lemma="pōja" cases="nom,acc" numbers="sg,pl" filter="positive.pre" />

<Note>
Kaksi huomiota sanasta <Hv l="kona">konor</Hv>. Ensinnäkin se on adjektiivin <Hv glossed>kona</Hv> ’tuo (elottomasta)’ vesisuvun muoto, joten sen monikko <Hv glossed>kōdra</Hv> tarkoittaa ’nuo’. Duolingon ohjeet kääntävät sen *these* ’nämä’, mutta oikein on *those* ’nuo’ (wikin sanakirja, sana *kona*: ”that (for inanimates)”). Toiseksi sanakirjassa <Hv glossed>konor</Hv> on myös oma substantiivinsa, ’höyry’. Sillä on sama äänneasu ja sama monikko <Hv l="konor" fi="höyryt">kōdra</Hv>, mutta se on eri sana.
</Note>

## Hänen: zȳha vai jāha?

Yksikön kolmannessa persoonassa valyriassa on kaksi persoonapronominia. <Hv glossed>Ziry</Hv> ’hän, se’ viittaa kuu- tai aurinkosuvun sanaan ja <Hv glossed>ūja</Hv> ’hän, se’ maa- tai vesisuvun sanaan. Omistussanoissa on sama jako. <Hv>Zȳha</Hv> on kuu- ja aurinkosukuista omistajaa varten ja <Hv l="jāha">jāha</Hv> maa- ja vesisukuista omistajaa varten. Duolingon ohjeet korostavat, että valinta tehdään omistajan suvun mukaan silloinkin, kun omistajaa ei mainita lauseessa:

- <Hv glossed>zȳhon lenton</Hv> ’hänen/sen talonsa’, kun omistaja on kuu- tai aurinkosukua
- <Hv glossed>jāhon lenton</Hv> ’hänen/sen talonsa’, kun omistaja on maa- tai vesisukua

Molemmissa pääte on -on, koska <Hv l="lenton">lenton</Hv> on maasukua. Omistussana mukautuu siis kahteen suuntaan yhtä aikaa: vartalo (<Hv>zȳh-</Hv> tai <Hv>jāh-</Hv>) tulee omistajasta, pääte omistetusta.

Tässä suomesta ei ole apua. Suomen *hän* ja *se* erottavat ihmisen muista, ja se muistuttaa hieman valyrian jakoa, koska useimmat ihmiset ovat kuu- tai aurinkosukua. Ohjeiden mukaan <Hv>zȳh-</Hv> onkin siksi todennäköisempi, kun omistaja on elollinen. Ratkaisee kuitenkin kieliopillinen suku eikä elollisuus. Maasuvun oppitunnin <Hv>Targārien</Hv> on ihminen mutta maasukua, joten hänestä puhuttaessa käytetään muotoa <Hv l="jāhon">jāhon</Hv>:

<Example id="ex-cd113b1637ad" />

Ilman asiayhteyttä, kuten useimmissa Duolingon lauseissa, kumpikin muoto on mahdollinen, ja merkitys on käytännössä sama.

Sarjassa jako näkyy selvästi. Lohikäärmeenhoitaja kertoo Vyörylän kaupungista, ja <Hv>Tōbleton</Hv> on maasukua. Siksi ’sen’ on kummassakin kohdassa <Hv>jāh-</Hv>. Pääte taas vaihtelee: <Hv>jārha</Hv> on vesisuvun monikossa, koska <Hv glossed>geralbar</Hv> ’katu, tie’ on vesisukua, ja <Hv glossed>jāhon gierion</Hv> ’sen kansa’ on maasuvun muodossa, koska <Hv>gierion</Hv> on maasukua. Huomaa myös <Hv>jārha</Hv>: tässä toimii sääntö 1 eli h + r → rh.

<Line id="hotd-s3e03-008" />

Repliikin <Hv glossed>geralbrari</Hv> ’kadut’ on kuitenkin kollektiivin akkusatiivi eikä monikko, joten se ei sovi yhteen monikkomuodon <Hv>jārha</Hv> kanssa. Wikin alaviitteen mukaan kyse on luultavasti virheestä. Säännöllinen monikon akkusatiivi olisi <Hv glossed>jārha geralbrī</Hv> ’sen kadut’.

## Omistuspronominit: zȳhon, jāhon, pōjon

Kun omistussana on yksinään ilman substantiivia (’minun omani’, ’hänen’), käytetään erityistä -on-muotoa. Wikin pronominisivun mukaan se taipuu kuin tavallinen maasuvun substantiivi: <Hv l="ñuhon">ñuhon</Hv>, <Hv l="aōhon">aōhon</Hv>, <Hv>zȳhon</Hv>, <Hv l="jāhon">jāhon</Hv>, <Hv l="īlvon">īlvon</Hv>, <Hv l="jevon">jevon</Hv>, <Hv l="pōjon">pōjon</Hv>.

<Pronounce word="zȳhon" />

Pronomini <Hv>zȳhon</Hv> näyttää samalta kuin adjektiivin <Hv>zȳha</Hv> maasuvun muoto, mutta sitä käytetään eri tavalla: yksinään tai predikatiivina.

<Example id="ex-de5d41f375ba" />

<Example id="ex-6e8f46d09a69" />

<Interlinear hv="Kon|a gelt|e jāh|on issa?" gloss="tuo-LUN.NOM kypärä-NOM hänen.omansa-NOM olla-PRS.3SG" fi="Onko tuo kypärä hänen?" />

<Hv>Kona</Hv> on kuusuvun muodossa, koska <Hv glossed>gelte</Hv> ’kypärä’ on kuusukua. <Hv l="jāhon">Jāhon</Hv> kertoo, että omistaja on maa- tai vesisukua.

## Lannisterit lähettävät terveisensä

Lopuksi tuttu lause:

<Example id="ex-cc7aa25c2069" />

<Interlinear hv="Lannister|i rytsurn|a pōj|a jittis." gloss="Lannister-NOM.PL tervehdys-ACC.PL heidän-TERR.ACC.PL lähettää-PRF.3PL" fi="Lannisterit lähettävät terveisensä." />

<Hv glossed>Rytsurnon</Hv> ’tervehdys’ on maasukua, ja sen monikko on <Hv>rytsurna</Hv>. Siksi ’heidän’ on maasuvun monikossa: <Hv l="pōja">pōja</Hv>. Tässä se tulee substantiivin perään eli jälkiasemaan. Valyriassa adjektiivi voi olla substantiivin edellä tai perässä. Verbi <Hv>jittis</Hv> on <Term id="perfekti">perfektissä</Term>, mutta siihen palataan myöhemmin.

<Practice hv="Zȳhon lenton urnen." fi="Näen hänen talonsa. (omistaja kuu- tai aurinkosukua)" />

<Practice hv="Jāhon lenton urnen." fi="Näen hänen talonsa. (omistaja maa- tai vesisukua)" />

<Practice hv="Pōja muña ēdrus." fi="Heidän äitinsä nukkuu." />

<Practice hv="Ziry ēdrus." fi="Hän nukkuu." />

## Lopuksi

Omistussana on adjektiivi, ja sen pääte tulee omistetusta sanasta. Yksikön kolmannessa persoonassa sen vartalo määräytyy lisäksi omistajan suvusta. Vesisuvun monikossa r muuttaa edeltävää konsonanttia: hr → rh, jr → j ja <Term id="nasaali">nasaali</Term> + r → pitkä vokaali + dr tai br. Seuraavaksi näitä tarvitaan perheen sanoissa.

## Perhe ja suku

Perheen ja suvun sanoja on paljon, seitsemäntoista, mutta kielioppia vähän. Sanat muodostavat kuitenkin järjestelmän, ja kun järjestelmän ymmärtää, sanat on helpompi muistaa. Duolingon Tips & Notes -ohjeiden mukaan sanasto on rakennettu niin, että sanat kuuluvat yhteen, ja ohjeet kehottavat etsimään sanapareista toistuvia kuvioita. Niin tässä tehdään.

## Isä on myös setä, äiti on myös täti

Valyrian sukulaisnimitykset ovat tarkempia kuin englannin. Wiki kutsuu järjestelmää irokeesityyppiseksi sukulaisuusjärjestelmäksi. Sen ydin on, että isän veli rinnastetaan isään ja äidin sisar äitiin:

- <Hv glossed>kepa</Hv> on ’isä’, mutta myös ’isän veli, setä’.
- <Hv glossed>muña</Hv> on ’äiti’, mutta myös ’äidin sisar’. Ohjeiden mukaan sana voi siis asiayhteydestä riippuen tarkoittaa myös tätiä.

Tästä seuraa, että heidän lapsensa eli niin sanotut rinnakkaisserkut ovat sisaruksia. Sanat ’veli’ ja ’sisko’ kattavat valyriassa myös isän veljen ja äidin sisaren lapset.

Suomalaisella on tähän paremmat lähtökohdat kuin englanninpuhujalla, koska suomessa setä ja eno ovat eri sanoja. Valyria tekee saman jaon kuin suomi mutta vie sen pidemmälle. Setä on suoraan sama sana kuin isä, kun taas enolle ja isän sisarelle on omat sanansa, ja ne jakautuvat vielä iän mukaan:

- <Hv glossed>iāpa</Hv> ’äidin vanhempi veli’ ja <Hv glossed>qȳbor</Hv> ’äidin nuorempi veli’ (eno)
- <Hv glossed>velma</Hv> ’isän vanhempi sisar’ ja <Hv glossed>ñāmar</Hv> ’isän nuorempi sisar’ (täti isän puolelta)

Suomen *täti* kattaa molemmat puolet. Valyriassa äidin sisar on <Hv>muña</Hv> ja isän sisar <Hv>velma</Hv> tai <Hv>ñāmar</Hv>, joten *täti*-sanaa ei voi kääntää ennen kuin tietää, kummalta puolelta täti on.

## Sisarukset iän mukaan

Valyria erottaa vanhemmat ja nuoremmat sisarukset eri sanoilla:

- <Hv glossed>lēkia</Hv> ’isoveli’ ja <Hv glossed>mandia</Hv> ’isosisko’
- <Hv glossed>valonqar</Hv> ’pikkuveli’ ja <Hv glossed>hāedar</Hv> ’pikkusisko’
- <Hv glossed>dubys</Hv> ’sisarus (ei kaksonen)’ ja <Hv glossed>idaña</Hv> ’kaksonen’

Neljä ensimmäistä tarkoittavat myös rinnakkaisserkkua. <Hv>Lēkia</Hv> on esimerkiksi myös isän veljen tai äidin sisaren poika, joka on itseä vanhempi. <Hv>Dubys</Hv> kattaa samoin rinnakkaisserkut. Suomen *isoveli* ja *pikkuveli* ovat yhdyssanoja, joten jako iän mukaan on suomalaiselle tuttu. Valyriassa sanoilla on kuitenkin kokonaan eri juuret.

<Pronounce word="valonqar" />

<Pronounce word="lēkia" />

Ohjeissa huomautetaan pareista vielä yhdestä kuviosta. Nuoremmat (<Hv>valonqar</Hv>, <Hv>hāedar</Hv>) ovat vesisukua, vanhemmat (<Hv>lēkia</Hv>, <Hv>mandia</Hv>) kuusukua. Suku on kieliopillinen ominaisuus, ja wikin pronominisivu mainitsee sanat <Hv>valonqar</Hv> ja <Hv>hāedar</Hv> esimerkkeinä elollisista vesisuvun sanoista. Siksi ’minun pikkusiskoni’ on vesisuvun muodossa, <Hv glossed>ñuhor hāedar</Hv>, mutta ’minun isosiskoni’ on <Hv glossed>ñuha mandia</Hv>.

<Example id="ex-521269ad7a00" />

<Interlinear hv="Ar|ia Sans|o hāed|ar issa." gloss="Arya-NOM Sansa-GEN pikkusisko-NOM olla-PRS.3SG" fi="Arya on Sansan pikkusisko." />

<Example id="ex-98000386884c" />

### Vesisuvun -ar-sanat taipuessaan

<Paradigm lemma="valonqar" cases="nom,acc,gen" numbers="sg,pl" />

<Hv>Valonqar</Hv> ja <Hv>hāedar</Hv> menettävät muissa muodoissa päätteensä a:n: <Hv>valonqri</Hv>, <Hv>valonqro</Hv>, <Hv>hāedri</Hv>, <Hv>hāedro</Hv>. Akkusatiivi <Hv>valonqri</Hv> esiintyy sarjassa, kun nuori Rhaenyra puhuu tulevasta pikkuveljestään:

<Line id="hotd-s1e01-016" />

Samassa jaksossa on myös genetiivi, ja omistussana on vesisuvun genetiivissä: <Hv glossed>ñurho valonqro</Hv> ’pikkuveljeni’. Tässä yhdistyvät genetiivi, omistussana ja vesisuvun hr → rh.

<Line id="hotd-s1e01-019" />

<Example id="ex-6768c822f817" />

## Ristiserkut: enon ja isän sisaren lapset

Rinnakkaisserkut ovat valyriassa sisaruksia. Ristiserkuilla eli äidin veljen ja isän sisaren lapsilla on omat sanansa. Wikin sukulaisuusliitteen mukaan ne on johdettu tädin tai enon nimityksestä:

- <Hv>iāpa</Hv> → <Hv glossed>iāpanna</Hv> ’äidin vanhemman veljen lapsi’
- <Hv>qȳbor</Hv> → <Hv glossed>qȳbranna</Hv> ’äidin nuoremman veljen lapsi’
- <Hv>velma</Hv> → <Hv glossed>velmanna</Hv> ’isän vanhemman sisaren lapsi’
- <Hv>ñāmar</Hv> → <Hv glossed>ñābranna</Hv> ’isän nuoremman sisaren lapsi’
- <Hv>dubāzma</Hv> on ristiserkun yleisnimitys.

Kaksi näistä pareista kannattaa katsoa tarkkaan. <Hv>Qȳbor</Hv> taipuu vartalolla <Hv>qȳbr-</Hv> (genetiivi <Hv>qȳbro</Hv>), ja sama vartalo näkyy sanassa <Hv>qȳbranna</Hv>. <Hv>Ñāmar</Hv> puolestaan noudattaa edellä opittua <Term id="nasaali">nasaalisääntöä</Term>. Kun m joutuu r:n eteen, siitä tulee b, joten akkusatiivi on <Hv>ñābri</Hv> ja serkku <Hv>ñābranna</Hv>. Vokaali on tässä valmiiksi pitkä, joten pitenemistä ei näy. Wikin äännerakennesivun mukaan kaikissa tunnetuissa mr-esimerkeissä vokaali on jo pitkä.

<Paradigm lemma="ñāmar" cases="nom,acc,gen" numbers="sg" />

<Pronounce word="ñābranna" />

<Pronounce word="iāpanna" />

Suomessa kaikki nämä ovat vain *serkkuja*. Antropologian termit *rinnakkaisserkku* ja *ristiserkku* ovat olemassa, mutta arkikieli ei tee tätä eroa, joten tässä suomesta ei ole apua. Hyvä muistisääntö on, että ristiserkun sana alkaa tädin tai enon sanan vartalolla. Ainoa poikkeus on yleisnimitys <Hv>dubāzma</Hv>.

<Example id="ex-3326482153df" />

<Example id="ex-07dc454e8d2a" />

<Example id="ex-11be124ca5b8" />

Viimeisessä esimerkissä <Hv glossed>aōhor</Hv> ’sinun’ on vesisuvun muodossa, koska <Hv>ñāmar</Hv> on vesisukua.

## Lapset

<Hv glossed>Tala</Hv> on ’tytär’, ja rinnakkaisperiaatteen mukaan myös naisen sisaren tai miehen veljen tytär. <Hv glossed>Rūs</Hv> on ’vauva, lapsi, jälkeläinen’. Se on aurinkosukua ja kuuluu wikin taivutussivun *rūs*-tyyppiin. Lyhyt muoto <Hv>rūs</Hv> on yksikön nominatiivi, akkusatiivi ja vokatiivi. Useimmissa muissa muodoissa vartalo on <Hv>rūh-</Hv>: genetiivi <Hv>rūho</Hv>, monikko <Hv>rūhossa</Hv>. Samalla tavalla taipuu kolme eläinten oppitunnin sanaa.

<Line id="got-s6e01-002" />

<Practice hv="Ñuha mandia ēdrus." fi="Isosiskoni nukkuu." />

<Practice hv="Ñuhor valonqar ēdrus." fi="Pikkuveljeni nukkuu." />

<Practice hv="Ñurho hāedro anne ēdrus." fi="Pikkusiskoni hevonen nukkuu." />

<Practice hv="Kepa valonqri urnes." fi="Isä näkee pikkuveljen." />

## Lopuksi

Valyrian perhe rakentuu kolmen jaon varaan: onko sukulainen isän vai äidin puolelta, onko hän vanhempi vai nuorempi ja onko hän rinnakkais- vai ristisukulainen. Setä on isä, äidin sisar on äiti, ja heidän lapsensa ovat sisaruksia. Enolla ja isän sisarella on omat sanansa, ja heidän lapsiaan tarkoittavat sanat on johdettu niistä. Ohjeiden mukaan Duolingo opettaa ensin rinnakkaisserkut ja myöhemmin ristiserkut. Tämän oppitunnin sanalistassa ovat jo molemmat.

## Sanat

<Vocab />

<Check />
